import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import config from "../config";
import Button from "./common/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "./common/Card";
import { Input } from "./common/Input";
import { Label } from "./common/Label";
import {
  Loader2,
  Shield,
  Trash2,
  UserRound,
  Mail,
  ArrowLeft,
  IdCard,
  X,
} from "lucide-react";
import customToast from "./common/Toaster";
import useBrandTranslation from "../hooks/useBrandTranslation";
import { apiJsonHeaders, withApiLocale } from "../utils/apiLocale";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from "./common/Dialog";

const ADMIN_EMAIL = "admin@aristas.com";
/** Actualización automática de la grilla (DNI, habilitados, whitelist) sin F5 */
const ADMIN_POLL_INTERVAL_MS = 30_000;

export default function AdminPanel() {
  const { t } = useBrandTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [professionals, setProfessionals] = useState([]);
  const [whitelist, setWhitelist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [dniViewerProfessional, setDniViewerProfessional] = useState(null);

  const api = config.baseUrlApi || "/api";

  const isAdminUser = useMemo(
    () =>
      !!user &&
      user.role === "admin" &&
      (user.email || "").toLowerCase() === ADMIN_EMAIL,
    [user]
  );

  const actingHeaders = useCallback(
    () => ({
      ...apiJsonHeaders(),
      "X-Acting-User-Id": user?.id || "",
    }),
    [user?.id]
  );

  const reloadAll = useCallback(async () => {
    const [pRes, wRes] = await Promise.all([
      fetch(`${api}/admin/professionals`, { headers: actingHeaders() }),
      fetch(`${api}/admin/whitelist`, { headers: actingHeaders() }),
    ]);
    const pJson = await pRes.json();
    const wJson = await wRes.json();
    if (!pRes.ok) throw new Error(pJson?.error || "Error");
    if (!wRes.ok) throw new Error(wJson?.error || "Error");
    setProfessionals(Array.isArray(pJson) ? pJson : []);
    setWhitelist(Array.isArray(wJson) ? wJson : []);
  }, [api, actingHeaders]);

  useEffect(() => {
    if (!isAdminUser) {
      navigate("/");
      return;
    }
    let cancelled = false;
    setLoading(true);
    reloadAll()
      .catch((e) => {
        if (!cancelled) customToast(String(e.message || e), "danger");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAdminUser, navigate, reloadAll]);

  useEffect(() => {
    if (!isAdminUser) return undefined;
    const tick = () => {
      if (document.visibilityState !== "visible") return;
      reloadAll().catch(() => {});
    };
    const id = window.setInterval(tick, ADMIN_POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [isAdminUser, reloadAll]);

  const toggleEnabled = async (professionalId, nextEnabled) => {
    setBusyId(professionalId);
    try {
      const res = await fetch(
        `${api}/admin/professionals/${professionalId}/enabled`,
        {
          method: "PATCH",
          headers: actingHeaders(),
          body: JSON.stringify({ is_professional_enabled: nextEnabled }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "No se pudo actualizar");
      await reloadAll();
      customToast(
        nextEnabled ? "Profesional habilitado." : "Profesional deshabilitado.",
        "success"
      );
    } catch (e) {
      customToast(String(e.message || e), "danger");
    } finally {
      setBusyId(null);
    }
  };

  const addWhitelist = async (e) => {
    e.preventDefault();
    const email = newEmail.trim().toLowerCase();
    if (!email.includes("@")) {
      customToast(t("app.admin.toastInvalidEmail", "Ingresá un email válido."), "warning");
      return;
    }
    try {
      const res = await fetch(`${api}/admin/whitelist`, {
        method: "POST",
        headers: actingHeaders(),
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok)
        throw new Error(data?.error || "No se pudo agregar");
      setNewEmail("");
      await reloadAll();
      customToast(t("app.admin.toastEmailAdded", "Email agregado a la lista."), "success");
    } catch (err) {
      customToast(String(err.message || err), "danger");
    }
  };

  const removeWhitelist = async (id) => {
    try {
      const res = await fetch(`${api}/admin/whitelist/${id}`, {
        method: "DELETE",
        headers: actingHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "No se pudo eliminar");
      await reloadAll();
      customToast(t("app.admin.toastEmailRemoved", "Email quitado de la lista."), "success");
    } catch (e) {
      customToast(String(e.message || e), "danger");
    }
  };

  if (!user || user.role !== "admin") return null;

  return (
    <div className="min-h-screen bg-gray-50 p-4 pb-16">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex flex-wrap items-center gap-4 justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-10 h-10 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Administración
              </h1>
              <p className="text-sm text-gray-600">
                Profesionales registrados y whitelist de altas. La lista se
                actualiza sola cada {ADMIN_POLL_INTERVAL_MS / 1000} s con la
                pestaña visible.
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={() => navigate("/")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Inicio
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
          </div>
        ) : (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserRound className="w-5 h-5" />
                  Profesionales registrados
                </CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-gray-600">
                      <th className="pb-2 pr-3">{t("app.admin.name", "Nombre")}</th>
                      <th className="pb-2 pr-3">{t("app.admin.email", "Email")}</th>
                      <th className="pb-2 pr-3">{t("app.admin.license", "Matrícula")}</th>
                      <th className="pb-2 pr-3">{t("app.admin.specialty", "Especialidad")}</th>
                      <th className="pb-2 pr-3">{t("app.admin.phone", "Teléfono")}</th>
                      <th className="pb-2 pr-3">DNI</th>
                      <th className="pb-2">{t("app.admin.operates", "Opera")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {professionals.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="py-8 text-center text-gray-500">
                          No hay profesionales registrados.
                        </td>
                      </tr>
                    ) : (
                      professionals.map((p) => (
                        <tr key={p.id} className="border-b border-gray-100">
                          <td className="py-3 pr-3 font-medium">
                            {p.name} {p.last_name}
                          </td>
                          <td className="py-3 pr-3">{p.email}</td>
                          <td className="py-3 pr-3">
                            {p.license_number || "—"}
                          </td>
                          <td className="py-3 pr-3">
                            {p.specialization || "—"}
                          </td>
                          <td className="py-3 pr-3">{p.phone || "—"}</td>
                          <td className="py-3 pr-3">
                            {p.dni_front_image || p.dni_back_image ? (
                              <button
                                type="button"
                                onClick={() => setDniViewerProfessional(p)}
                                className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                <IdCard className="h-4 w-4 shrink-0" />
                                Ver fotos DNI
                              </button>
                            ) : (
                              <span className="text-gray-400">{t("app.admin.notUploaded", "Sin cargar")}</span>
                            )}
                          </td>
                          <td className="py-3">
                            <label className="inline-flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                className="rounded border-gray-300 h-4 w-4 text-blue-600 focus:ring-blue-500"
                                checked={p.is_professional_enabled !== false}
                                disabled={busyId === p.id}
                                onChange={(e) =>
                                  toggleEnabled(p.id, e.target.checked)
                                }
                              />
                              {busyId === p.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <span className="text-xs text-gray-600">
                                  {p.is_professional_enabled !== false
                                    ? "Habilitado"
                                    : "Suspendido"}
                                </span>
                              )}
                            </label>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Emails autorizados para registrarse como profesional
                </CardTitle>
                <p className="text-sm text-gray-600 font-normal mt-1">
                  Solo quienes figuren aquí pueden completar el registro como
                  médico. El email debe coincidir exactamente (sin importar
                  mayúsculas).
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <form
                  onSubmit={addWhitelist}
                  className="flex flex-col sm:flex-row gap-3 items-end"
                >
                  <div className="flex-1 w-full space-y-2">
                    <Label htmlFor="whitelist-email">{t("app.admin.addEmail", "Agregar email")}</Label>
                    <Input
                      id="whitelist-email"
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder={t("app.admin.emailPlaceholder", "correo@ejemplo.com")}
                    />
                  </div>
                  <Button type="submit">{t("app.commonUi.add", "Agregar")}</Button>
                </form>

                <ul className="divide-y rounded-lg border bg-white">
                  {whitelist.length === 0 ? (
                    <li className="p-4 text-gray-500 text-sm">
                      La lista está vacía: nadie puede registrarse como
                      profesional hasta que agregues emails.
                    </li>
                  ) : (
                    whitelist.map((row) => (
                      <li
                        key={row.id}
                        className="flex items-center justify-between gap-2 p-3"
                      >
                        <span className="font-mono text-sm">{row.email}</span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="shrink-0 text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => removeWhitelist(row.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </li>
                    ))
                  )}
                </ul>
              </CardContent>
            </Card>

            <Dialog
              open={!!dniViewerProfessional}
              onOpenChange={(open) => {
                if (!open) setDniViewerProfessional(null);
              }}
            >
              <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl p-6">
                {dniViewerProfessional && (
                  <>
                    <div className="mb-4 flex items-start justify-between gap-3">
                      <DialogTitle className="text-lg font-bold text-gray-900 pr-8">
                        Fotos DNI — {dniViewerProfessional.name}{" "}
                        {dniViewerProfessional.last_name}
                      </DialogTitle>
                      <DialogClose asChild>
                        <button
                          type="button"
                          className="shrink-0 rounded-full p-1 text-gray-500 transition-colors hover:bg-gray-100"
                          aria-label={t("common.close", "Cerrar")}
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </DialogClose>
                    </div>
                    <p className="mb-4 text-sm text-gray-600">
                      Imágenes que el profesional cargó para verificación de
                      identidad.
                    </p>
                    <div className="space-y-6">
                      {dniViewerProfessional.dni_front_image ? (
                        <div>
                          <p className="mb-2 text-sm font-medium text-gray-700">
                            Frente
                          </p>
                          <a
                            href={dniViewerProfessional.dni_front_image}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block text-xs text-blue-600 hover:underline mb-2"
                          >
                            Abrir en pestaña nueva
                          </a>
                          <img
                            src={dniViewerProfessional.dni_front_image}
                            alt={`DNI frente — ${dniViewerProfessional.name}`}
                            className="max-h-[50vh] w-full rounded-lg border border-gray-200 object-contain bg-gray-50"
                          />
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">{t("app.admin.noFrontPhoto", "Sin foto de frente.")}</p>
                      )}
                      {dniViewerProfessional.dni_back_image ? (
                        <div>
                          <p className="mb-2 text-sm font-medium text-gray-700">
                            Dorso
                          </p>
                          <a
                            href={dniViewerProfessional.dni_back_image}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block text-xs text-blue-600 hover:underline mb-2"
                          >
                            Abrir en pestaña nueva
                          </a>
                          <img
                            src={dniViewerProfessional.dni_back_image}
                            alt={`DNI dorso — ${dniViewerProfessional.name}`}
                            className="max-h-[50vh] w-full rounded-lg border border-gray-200 object-contain bg-gray-50"
                          />
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">{t("app.admin.noBackPhoto", "Sin foto de dorso.")}</p>
                      )}
                    </div>
                  </>
                )}
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>
    </div>
  );
}
